import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { StationItemNo } from '../../../../constants/Games/gameStation';
import { getStationVisual } from '../../../../constants/Games/gameStationUtils';
import { SVGIconType } from '../../../../constants/svgIcon';
import { NOT_APPLICABLE_STRING } from '../../../../constants/general';
import { Icons, IconType } from '../../../../constants/icons';
import { Profile } from '../../../../models/UserStore';
import { Space } from '../../../../models/SpaceStore';

import { SeparatorComponent } from '../../SeparatorComponent';
import { AvatarNode } from '../../AvatarNode';
import { SvgIcon } from '../../SVGIcon';
import { PngIcon } from '../../PngIcon';
import './ChallengeStationCell.css';

export type ChallengeStationCellProps = {
  stationId: StationItemNo;
  albumClassroom: Space;
  name: string | undefined;
  index: number;
  isMiniChallenge: boolean;
  playCount: number;
  averageScore: number;
  remainingJCoinReward: number | undefined;
  remainingSpaceReward: number | undefined;
  userProfile: Profile;
  recordScore: number | undefined;
  recordDuration: string | undefined;
  isUnlocked: boolean;
  onClickPlay: () => void;
};

export const ChallengeStationCell = memo((props: ChallengeStationCellProps) => {
  const {
    stationId,
    albumClassroom,
    name,
    index,
    isMiniChallenge,
    playCount,
    averageScore,
    remainingJCoinReward,
    remainingSpaceReward,
    userProfile,
    recordScore,
    recordDuration,
    isUnlocked,
    onClickPlay,
  } = props;
  const { t } = useTranslation();

  return (
    <div className="challenge-station-cell" onClick={onClickPlay}>
      <div className="centered-container">
        <div className="lower-row">
          <div className="component">
            <img
              src={getStationVisual(stationId)?.filePrefix}
              className="large-icon"
              alt="Large Icon"
            />
            <div className="text-overlay">{name ?? index}</div>
            {remainingSpaceReward || remainingJCoinReward ? (
              <div className="reward-bar">
                {remainingSpaceReward ? (
                  <div className="reward">
                    <div className="image-stack">
                      <img
                        src={Icons[IconType.SpaceCreditFrame]}
                        className="frame-image"
                      />
                      <img
                        src={albumClassroom?.icon?.publicUrls?.small}
                        className="space-image"
                      />
                    </div>
                    <div className="reward-text">{remainingSpaceReward}</div>
                  </div>
                ) : null}
                {remainingJCoinReward ? (
                  <div className="reward">
                    <PngIcon name="JCoin" className="reward-icon" />
                    <div className="reward-text">{remainingJCoinReward}</div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div
            className={`game-component ${isUnlocked ? 'unlocked' : 'locked'}`}
          >
            <div className="upper-part">
              <div className="left-part">
                <div className="title-part">
                  <SvgIcon
                    name={
                      isMiniChallenge
                        ? SVGIconType.MiniTarget
                        : SVGIconType.SuperTarget
                    }
                    className={`challenge-icon grey-text`}
                  />
                  <div className="title">
                    {isMiniChallenge
                      ? t('Mini Challenge')
                      : t('Super Challenge')}
                  </div>
                </div>
                <div className="info">
                  <SeparatorComponent
                    separator={<p className={`separate-text grey-text`}>·</p>}
                  >
                    <div className={`reward-text grey-text`}>
                      {t('{count} Plays', {
                        count: playCount,
                      })}
                    </div>
                    <div className="reward">
                      <SvgIcon
                        name={SVGIconType.Score}
                        className={`reward-icon grey-text`}
                      />
                      <div className={`reward-text grey-text`}>
                        {averageScore}
                      </div>
                    </div>
                  </SeparatorComponent>
                </div>
              </div>
            </div>
            {recordScore !== undefined || recordDuration !== undefined ? (
              <div className="lower-part">
                <AvatarNode
                  avatar={{
                    ...userProfile.avatar,
                    userId: userProfile.userId,
                    source:
                      userProfile.profilePicture &&
                      userProfile.profilePicture.publicUrls?.small
                        ? {
                            uri: userProfile.profilePicture.publicUrls.small,
                          }
                        : NOT_APPLICABLE_STRING,
                  }}
                  size={20}
                />
                <div className="reward">
                  <SvgIcon
                    name={SVGIconType.Score}
                    className={`reward-icon grey-text`}
                  />
                  <div className={`reward-text grey-text`}>{recordScore}</div>
                </div>
                <div className="reward">
                  <SvgIcon
                    name={SVGIconType.Timer}
                    className={`reward-icon grey-text`}
                  />
                  <div className={`reward-text grey-text`}>
                    {recordDuration}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
});
